import { Typography } from "@circle-react-uikit/Typography";

import { t } from "@/i18n-js/instance";
import { Badge } from "@/react/components/SettingsApp/CompassCompat/Badge";

import { usePlans } from "../../RegularPlan/PlansContext";
import { SubmitButton } from "../../RegularPlan/SubmitButton";
import { LineItem } from "./PlanInfo/LineItem";
import { PlanSummaryContainer } from "./PlanSummaryContainer";
import { PlanSummarySkeleton } from "./PlanSummarySkeleton";

const BILLING_LABEL_TRANSLATIONS: Record<string, () => string> = {
  year: () => t("pricing_plans.plan_settings.plan_summary.annual_billing"),
  biannual: () =>
    t("pricing_plans.plan_settings.plan_summary.biannual_billing"),
  quarter: () =>
    t("pricing_plans.plan_settings.plan_summary.quarterly_billing"),
};

const BILLING_TOTAL_TRANSLATIONS: Record<string, (amount: string) => string> = {
  year: amount =>
    t("pricing_plans.plan_settings.plan_summary.plan_annual_total", {
      amount,
    }),
  biannual: amount =>
    t("pricing_plans.plan_settings.plan_summary.plan_biannual_total", {
      amount,
    }),
  quarter: amount =>
    t("pricing_plans.plan_settings.plan_summary.plan_quarterly_total", {
      amount,
    }),
};

export const PlusCustomOfferSummary = () => {
  const { selectedPlan, customPlanOfferPlan } = usePlans();
  if (!selectedPlan || !customPlanOfferPlan) return <PlanSummarySkeleton />;

  const { interval } = selectedPlan;
  const billingLabelTranslation =
    BILLING_LABEL_TRANSLATIONS[interval] ?? BILLING_LABEL_TRANSLATIONS.year;
  const billingTotalTranslation =
    BILLING_TOTAL_TRANSLATIONS[interval] ?? BILLING_TOTAL_TRANSLATIONS.year;

  const {
    discount_percentage: discountPercentage,
    display_annual_total: annualTotal,
    display_annual_total_before_discount: annualTotalBeforeDiscount,
    display_annual_savings: annualSavings,
    display_total: dueTodayDisplay,
  } = customPlanOfferPlan;
  const hasDiscount = discountPercentage > 0;

  return (
    <PlanSummaryContainer>
      <Typography.LabelXl weight="semibold">
        {t("pricing_plans.plan_settings.plan_summary.title")}
      </Typography.LabelXl>

      <div className="flex flex-col gap-0.5">
        <div className="flex flex-row items-center gap-2">
          <Typography.LabelSm weight="semibold">
            {billingLabelTranslation()}
          </Typography.LabelSm>
          {hasDiscount && (
            <Badge
              variant="secondary"
              label={t(
                "pricing_plans.plan_settings.plan_summary.discount_savings_badge",
                {
                  percentage: discountPercentage,
                },
              )}
              className="border-green-200 bg-green-50 text-xs text-green-700"
            />
          )}
        </div>
        <Typography.BodySm color="text-default">
          {hasDiscount && (
            <span className="mr-1 line-through">
              {annualTotalBeforeDiscount}
            </span>
          )}
          {billingTotalTranslation(
            hasDiscount ? annualTotal : annualTotalBeforeDiscount,
          )}
        </Typography.BodySm>
      </div>

      <div className="border-primary flex flex-col gap-y-1 border-t pt-4">
        {hasDiscount && (
          <LineItem
            description={t(
              "pricing_plans.plan_settings.plan_summary.discount_savings_badge",
              {
                percentage: discountPercentage,
              },
            )}
            value={`-${annualSavings}`}
            valueProps={{ color: "text-green-success" }}
          />
        )}
        <LineItem
          description={t("pricing_plans.plan_settings.plan_summary.due_today")}
          value={dueTodayDisplay}
          descriptionProps={{ weight: "font-medium" }}
          valueProps={{ weight: "font-medium" }}
        />
      </div>

      <SubmitButton plan={selectedPlan} />

      <Typography.BodyXs color="text-light">
        {t("pricing_plans.plan_settings.plan_summary.terms_prefix")}{" "}
        <a
          href="https://circle.so/creator-terms"
          target="_blank"
          rel="noreferrer"
          className="underline"
        >
          {t("pricing_plans.plan_settings.plan_summary.terms_link")}
        </a>
        {". "}
        {t("pricing_plans.plan_settings.plan_summary.terms_suffix")}
      </Typography.BodyXs>
    </PlanSummaryContainer>
  );
};
